/**
 * SubscriptionWidget - class-based logic for product subscription options.
 * Listens to variant:change, updates option prices and syncs selected price to ATC button.
 */
class SubscriptionWidget {
  /**
   * @param {HTMLElement} root - Widget root element (#subscription-widget-*)
   */
  constructor(root) {
    this.root = root;
    if (!root) return;

    this.formId = root.dataset.formId || '';
    this.moneyFormat = this._parseMoneyFormat(root.dataset.moneyFormat);
    this.variantsData = this._parseVariantsData(root);
    this.form = document.getElementById(this.formId);

    this._sellingPlanInput = null;
    this._propertyInput = null;
    this._currentVariantId = null;

    this._bindElements();
    this._bindEvents();
    this._initFromForm();
  }

  _parseMoneyFormat(str) {
    try {
      return JSON.parse(str || '"${{amount}}"');
    } catch (_) {
      return '${{amount}}';
    }
  }

  _parseVariantsData(root) {
    const script = root.querySelector('script[type="application/json"][data-subscription-variants]');
    if (!script || !script.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (_) {
      return {};
    }
  }

  _bindElements() {
    this.options = Array.from(this.root.querySelectorAll('.subscription-widget__option')).map(optEl => {
      const radio = optEl.querySelector('.subscription-widget__radio');
      const priceEl = optEl.querySelector('[data-option-price]');
      const optionId = radio ? (radio.value === 'one-time' ? 'one-time' : radio.value) : null;
      return { optEl, radio, priceEl, optionId };
    }).filter(o => o.radio && o.optionId);

    // Prefer the dedicated hidden input inside the associated product form
    this._sellingPlanInput = this.form?.querySelector('[data-subscription-selling-plan]') || null;
  }

  _bindEvents() {
    this.options.forEach(({ radio, optionId }) => {
      radio.addEventListener('change', () => this._onOptionChange(optionId));
    });

    if (this.form) {
      this.form.addEventListener('variant:change', (e) => this._onVariantChange(e));
      // Ensure selling_plan matches currently selected option on submit
      this.form.addEventListener('submit', () => {
        const checked = this.root.querySelector('.subscription-widget__radio:checked');
        const optionId = checked ? (checked.value === 'one-time' ? 'one-time' : checked.value) : 'one-time';
        this._onOptionChange(optionId);
      });
    }

    document.addEventListener('variant:change', (e) => {
      if (e.detail?.formId === this.formId) this._onVariantChange(e);
    });

    const infoButtons = this.root.querySelectorAll('[data-subscription-info-trigger]');
    infoButtons.forEach(button => {
      const option = button.closest('.subscription-widget__option');
      const modal = option ? option.querySelector('[data-subscription-info-modal]') : null;
      if (!modal) return;

      const closeEls = modal.querySelectorAll('[data-subscription-info-close]');
      const openModal = () => {
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
      };
      const closeModal = () => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
      };

      button.addEventListener('click', openModal);
      closeEls.forEach(el => el.addEventListener('click', closeModal));
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });
    });
  }

  _onVariantChange(e) {
    const variant = e?.detail?.variant;
    if (!variant || variant.id == null) return;
    this._currentVariantId = variant.id;
    this._updatePricesForVariant(variant);
    this._reapplySelectedOptionPrice();
  }

  _onOptionChange(optionId) {
    this._updateSellingPlanInput(optionId);
    this._updatePropertyInput(optionId);
    this._updateButtonPrice(optionId);
    this.root.dispatchEvent(new CustomEvent('subscription:change', {
      bubbles: true,
      detail: { frequency: optionId }
    }));
  }

  _initFromForm() {
    const idInput = this.form?.querySelector('input[name="id"]');
    const variantId = idInput ? String(idInput.value).trim() : null;
    if (variantId && this.variantsData[variantId]) {
      this._currentVariantId = variantId;
      const data = this.variantsData[variantId];
      this._updatePricesForVariant({ id: variantId, price: data.price });
    }
    const checked = this.root.querySelector('.subscription-widget__radio:checked');
    if (checked) {
      this._updateSellingPlanInput(checked.value);
      this._updatePropertyInput(checked.value);
      this._updateButtonPrice(checked.value);
    }
  }

  _getPriceForVariantAndOption(variantId, optionId) {
    const data = this.variantsData[variantId];
    if (!data || data.price == null) return null;
    if (optionId === 'one-time') return data.price;
    return data.allocations && data.allocations[optionId] != null
      ? data.allocations[optionId]
      : data.price;
  }

  _updatePricesForVariant(variant) {
    const variantId = variant.id;
    const data = this.variantsData[variantId];
    const basePrice = variant.price != null ? variant.price : (data && data.price);
    if (basePrice == null) return;

    this.options.forEach(({ optionId, priceEl, optEl }) => {
      const price = this._getPriceForVariantAndOption(String(variantId), optionId);
      if (priceEl && price != null) {
        priceEl.textContent = this.formatMoney(price);
      }
      const compareEl = optEl?.querySelector('[data-option-compare]');
      if (compareEl) {
        compareEl.textContent = this.formatMoney(basePrice);
      }
      const savingsEl = optEl?.querySelector('[data-option-savings]');
      if (savingsEl && optionId !== 'one-time' && price != null) {
        const savings = Math.round(basePrice - price);
        savingsEl.textContent = savings > 0 ? this.formatMoney(savings) : '';
      }
    });

    const originalEls = this.root.querySelectorAll('.subscription-widget__original-price');
    originalEls.forEach(el => {
      el.textContent = this.formatMoney(basePrice);
    });

    const checked = this.root.querySelector('.subscription-widget__radio:checked');
    if (checked) {
      this._updateButtonPrice(checked.value);
    }
  }

  _updateSellingPlanInput(optionId) {
    if (!this.form) return;
    if (!this._sellingPlanInput) {
      // Fallback: try to find or create in the form if widget-scoped input is missing
      this._sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
      if (!this._sellingPlanInput) {
        this._sellingPlanInput = document.createElement('input');
        this._sellingPlanInput.type = 'hidden';
        this._sellingPlanInput.name = 'selling_plan';
        this.form.appendChild(this._sellingPlanInput);
      }
    }
    if (optionId === 'one-time') {
      this._sellingPlanInput.removeAttribute('value');
      this._sellingPlanInput.disabled = true;
    } else {
      this._sellingPlanInput.value = optionId;
      this._sellingPlanInput.disabled = false;
    }
  }

  _updatePropertyInput(optionId) {
    if (!this.form) return;
    if (!this._propertyInput) {
      this._propertyInput = this.form.querySelector('input[name="properties[Subscription Frequency]"]');
      if (!this._propertyInput) {
        this._propertyInput = document.createElement('input');
        this._propertyInput.type = 'hidden';
        this._propertyInput.name = 'properties[Subscription Frequency]';
        this.form.appendChild(this._propertyInput);
      }
    }
    this._propertyInput.value = optionId;
  }

  _updateButtonPrice(optionId) {
    const variantId = this._currentVariantId || this.form?.querySelector('input[name="id"]')?.value;
    const price = variantId ? this._getPriceForVariantAndOption(String(variantId), optionId) : null;
    if (price == null) return;

    const btn = document.querySelector('.custom-add-to-cart__btn');
    const salePriceEl = btn?.querySelector('sale-price');
    if (salePriceEl) {
      salePriceEl.textContent = this.formatMoney(price);
    }

    this._updateMainPrice(optionId);
  }

  _updateMainPrice(optionId) {
    const variantId = this._currentVariantId || this.form?.querySelector('input[name="id"]')?.value;
    if (!variantId || !this.variantsData[variantId]) return;
    const data = this.variantsData[variantId];
    const basePrice = data.price;
    if (basePrice == null) return;

    const price = this._getPriceForVariantAndOption(String(variantId), optionId) ?? basePrice;

    const lists = Array.from(document.querySelectorAll('.price-list--product'));
    const priceList = lists.find(l => !l.closest('.custom-add-to-cart__btn')) || lists[0];
    if (!priceList) return;

    const saleEl = priceList.querySelector('sale-price');
    const originalEl = priceList.querySelector('[data-subscription-original]');
    const badge = priceList.querySelector('[data-subscription-badge]');
    const badgeText = priceList.querySelector('[data-subscription-badge-text]');

    if (saleEl) {
      saleEl.textContent = this.formatMoney(price);
    }

    if (originalEl) {
      if (optionId === 'one-time' || price >= basePrice) {
        originalEl.hidden = true;
      } else {
        originalEl.textContent = this.formatMoney(basePrice);
        originalEl.hidden = false;
      }
    }

    if (badge && badgeText) {
      if (optionId === 'one-time' || price >= basePrice) {
        badge.hidden = true;
      } else {
        const percent = Math.round(100 - price / basePrice * 100);
        if (percent > 0) {
          badgeText.textContent = `Save ${percent}% now`;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    }
  }

  _reapplySelectedOptionPrice() {
    const checked = this.root.querySelector('.subscription-widget__radio:checked');
    if (!checked) return;
    const optionId = checked.value === 'one-time' ? 'one-time' : checked.value;
    // Some theme scripts rewrite ATC/main prices after variant switch.
    // Re-apply selected subscription price after those handlers run.
    requestAnimationFrame(() => this._updateButtonPrice(optionId));
    window.setTimeout(() => this._updateButtonPrice(optionId), 120);
    // The legacy SLS URL-polling interval fires at ~500ms and can override the price.
    // A 600ms re-apply wins that race without interfering with other logic.
    window.setTimeout(() => this._updateButtonPrice(optionId), 600);
  }

  /**
   * @param {number} cents - Price in currency subunits
   * @returns {string} Formatted price string
   */
  formatMoney(cents) {
    const value = (Math.round(cents) / 100).toFixed(2);
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','));
  }
}

function initSubscriptionWidgets() {
  document.querySelectorAll('[data-subscription-widget]').forEach(root => {
    if (root.SubscriptionWidget) return;
    const instance = new SubscriptionWidget(root);
    root.SubscriptionWidget = instance;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubscriptionWidgets);
} else {
  initSubscriptionWidgets();
}

document.addEventListener('shopify:section:load', initSubscriptionWidgets);
